import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';
import { getNoticeAuthorDisplay } from '../../utils/noticeAuthor';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';
const PAGE_SIZE = 10;

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 900px) {
    border-radius: 1rem;
    padding: 1rem;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const SearchToolbar = styled.form`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.85rem;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  max-width: 420px;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  padding: 0.45rem 0.7rem;
  font-size: 0.88rem;

  @media (max-width: 900px) {
    min-width: 0;
    max-width: none;
    width: 100%;
  }
`;

const SecondaryBtn = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 0.55rem;
  padding: 0.45rem 0.85rem;
  font-weight: 600;
  font-size: 0.86rem;
  cursor: pointer;
  color: #475569;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 0.65rem;
  padding: 0.5rem 1rem;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  background: rgba(99, 102, 241, 0.95);
  color: #f8fafc;
  transition: filter 0.15s ease;
  &:hover {
    filter: brightness(1.06);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ComposePanel = styled.div`
  margin-top: 1rem;
  padding: 1.25rem;
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  background: rgba(15, 23, 42, 0.03);
`;

const Field = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 0.35rem;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  padding: 0.5rem 0.65rem;
  font-size: 0.92rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 140px;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  padding: 0.55rem 0.65rem;
  font-size: 0.92rem;
  resize: vertical;
`;

const ComposeFooter = styled.div`
  margin-top: 0.25rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(148, 163, 184, 0.35);
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const ComposeError = styled.span`
  font-size: 0.85rem;
  color: #b91c1c;
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  margin-top: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 900px) {
    display: none;
  }
`;

const Th = styled.th`
  background: #f8fafc;
  color: #64748b;
  text-align: left;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.65rem 0.75rem;
  white-space: nowrap;
`;

const ThNum = styled(Th)`
  text-align: right;
  width: 4rem;
`;

const ThSeq = styled(Th)`
  text-align: center;
  width: 3rem;
  font-variant-numeric: tabular-nums;
`;

const Td = styled.td`
  border-top: 1px solid #f1f5f9;
  color: #1f2937;
  font-size: 0.86rem;
  padding: 0.65rem 0.75rem;
  vertical-align: middle;
`;

const TdNum = styled(Td)`
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #475569;
`;

const TdSeq = styled(Td)`
  text-align: center;
  font-variant-numeric: tabular-nums;
  color: #64748b;
  font-weight: 600;
`;

const TitleLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
  font-weight: 600;
  color: ${({ $removed }) => ($removed ? '#94a3b8' : '#0f172a')};
  text-decoration: none;
  line-height: 1.35;
  font-style: ${({ $removed }) => ($removed ? 'italic' : 'normal')};
  &:hover {
    text-decoration: underline;
    color: #4f46e5;
  }
`;

const AuthorWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Avatar = styled.span`
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  background: rgba(248, 250, 252, 0.9);
  flex-shrink: 0;
`;

const AuthorMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`;

const AuthorPrimary = styled.span`
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
`;

const AuthorSecondary = styled.span`
  font-size: 0.72rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
`;

const MobileList = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: grid;
    gap: 0.55rem;
    padding: 0.6rem;
  }
`;

const MobileCard = styled.article`
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.65rem;
  background: #fff;
`;

const MobileTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
`;

const MobileSeq = styled.span`
  flex-shrink: 0;
  min-width: 1.5rem;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
`;

const MobileMetaGrid = styled.div`
  margin-top: 0.55rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25rem 0.55rem;
  font-size: 0.8rem;
  color: #475569;
`;

const Pager = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.75rem;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const PageBtn = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 0.6rem;
  padding: 0.35rem 0.65rem;
  cursor: pointer;
  font-size: 0.85rem;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Empty = styled.p`
  margin: 0;
  padding: 1.5rem;
  color: #64748b;
  text-align: center;
`;

const ErrorText = styled.p`
  margin: 0.75rem 0 0;
  color: #b91c1c;
  font-size: 0.9rem;
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.login_id ? parsed : null;
  } catch {
    return null;
  }
};

const canWriteStudentBoard = (roles = []) =>
  ['student', 'teacher', 'admin', 'super_admin'].some((r) => roles.includes(r));

const formatDt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
};

const AuthorCell = ({ author }) => {
  const { primary, secondary } = getNoticeAuthorDisplay(author);
  return (
    <AuthorWrap>
      <Avatar aria-hidden>{author?.profile_emoji || '🙂'}</Avatar>
      <AuthorMeta>
        <AuthorPrimary title={primary}>{primary}</AuthorPrimary>
        {secondary ? <AuthorSecondary title={secondary}>{secondary}</AuthorSecondary> : null}
      </AuthorMeta>
    </AuthorWrap>
  );
};

const PortalBoardPage = () => {
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [composeMsg, setComposeMsg] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const canWrite = canWriteStudentBoard(portalUser?.roles || []);

  const fetchPosts = useCallback(
    async (targetPage) => {
      if (!portalUser?.login_id) return;
      setError('');
      try {
        const safePage = Math.max(1, parseInt(String(targetPage ?? 1), 10) || 1);
        const qs = new URLSearchParams({
          login_id: portalUser.login_id,
          page: String(safePage),
          page_size: String(PAGE_SIZE),
        });
        if (appliedSearch) qs.set('q', appliedSearch);
        const data = await request({ endpoint: `${BACKEND_BASE_URL}/api/student-board/posts?${qs.toString()}` });
        setPosts(data?.posts || []);
        setTotalPages(Math.max(1, data?.total_pages || 1));
        setTotal(data?.total ?? 0);
      } catch (e) {
        setError(e?.responseData?.message || '게시글 목록을 불러오지 못했습니다.');
      }
    },
    [portalUser?.login_id, request, appliedSearch],
  );

  useEffect(() => {
    fetchPosts(page);
  }, [page, fetchPosts]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedSearch(searchDraft.trim());
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchDraft('');
    setAppliedSearch('');
    setPage(1);
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    setComposeMsg('');
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/student-board/posts`,
        method: 'POST',
        body: {
          login_id: portalUser.login_id,
          title: title.trim(),
          content: content.trim(),
        },
      });
      setTitle('');
      setContent('');
      setComposeOpen(false);
      setPage(1);
      await fetchPosts(1);
    } catch (err) {
      setComposeMsg(err?.responseData?.message || '게시글 등록에 실패했습니다.');
    }
  };

  const emptyMessage = appliedSearch
    ? `"${appliedSearch}"에 대한 검색 결과가 없습니다.`
    : '등록된 게시글이 없습니다.';

  return (
    <Wrapper>
      <SectionHeading
        eyebrow="BOARD"
        title="학생 자유 게시판"
        description="코딩학원 친구들과 선생님께 하고 싶은 이야기를 편하게 나눠 보세요. 오늘의 소감이나 궁금한 점도 좋아요."
      />
      <Toolbar>
        <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
          총 <strong>{total}</strong>건
        </span>
        {canWrite ? (
          <PrimaryButton type="button" onClick={() => setComposeOpen((v) => !v)}>
            {composeOpen ? '작성 닫기' : '글쓰기'}
          </PrimaryButton>
        ) : null}
      </Toolbar>

      <SearchToolbar onSubmit={handleSearchSubmit}>
        <SearchInput
          id="board-list-search"
          type="search"
          value={searchDraft}
          onChange={(ev) => setSearchDraft(ev.target.value)}
          placeholder="제목·내용 검색"
          autoComplete="off"
          aria-label="게시판 제목·내용 검색"
        />
        <SecondaryBtn type="submit" disabled={loading}>
          검색
        </SecondaryBtn>
        {appliedSearch ? (
          <SecondaryBtn type="button" onClick={handleClearSearch} disabled={loading}>
            초기화
          </SecondaryBtn>
        ) : null}
      </SearchToolbar>

      {canWrite && composeOpen ? (
        <ComposePanel as="form" onSubmit={handleSubmitPost}>
          <Field>
            <Label htmlFor="board-title">제목</Label>
            <Input
              id="board-title"
              value={title}
              onChange={(ev) => setTitle(ev.target.value)}
              maxLength={200}
              placeholder="제목 (최대 200자)"
              required
            />
          </Field>
          <Field style={{ marginBottom: 0 }}>
            <Label htmlFor="board-content">내용</Label>
            <TextArea
              id="board-content"
              value={content}
              onChange={(ev) => setContent(ev.target.value)}
              placeholder="내용을 입력하세요."
              required
            />
          </Field>
          <ComposeFooter>
            <PrimaryButton type="submit" disabled={loading}>
              등록
            </PrimaryButton>
          </ComposeFooter>
          {composeMsg ? <ComposeError>{composeMsg}</ComposeError> : null}
        </ComposePanel>
      ) : null}

      <TableWrap>
        {posts.length === 0 && !loading ? (
          <Empty>{emptyMessage}</Empty>
        ) : posts.length === 0 ? (
          <Empty>불러오는 중...</Empty>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <ThSeq>No.</ThSeq>
                  <Th>제목</Th>
                  <Th>작성자</Th>
                  <ThNum>조회</ThNum>
                  <ThNum>좋아요</ThNum>
                  <Th>작성일</Th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p, idx) => (
                  <tr key={p.id}>
                  <TdSeq>{Math.max(1, total - ((page - 1) * PAGE_SIZE + idx))}</TdSeq>
                    <Td>
                      <TitleLink to={`/portal/board/${p.id}`} $removed={p.is_moderation_removed}>
                        <span style={{ minWidth: 0 }}>{p.title}</span>
                      </TitleLink>
                    </Td>
                    <Td>
                      <AuthorCell author={p.author} />
                    </Td>
                    <TdNum>{p.view_count ?? 0}</TdNum>
                    <TdNum>{p.like_count ?? 0}</TdNum>
                    <Td>{formatDt(p.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <MobileList>
              {posts.map((p, idx) => {
                const seq = Math.max(1, total - ((page - 1) * PAGE_SIZE + idx));
                const { primary, secondary } = getNoticeAuthorDisplay(p.author);
                return (
                  <MobileCard key={`m-${p.id}`}>
                    <MobileTitleRow>
                      <MobileSeq>No.{seq}</MobileSeq>
                      <TitleLink to={`/portal/board/${p.id}`} $removed={p.is_moderation_removed}>
                        <span style={{ minWidth: 0 }}>{p.title}</span>
                      </TitleLink>
                    </MobileTitleRow>
                    <MobileMetaGrid>
                      <span>작성자: {primary}</span>
                      <span>조회: {p.view_count ?? 0}</span>
                      <span>좋아요: {p.like_count ?? 0}</span>
                      <span>{formatDt(p.created_at)}</span>
                    </MobileMetaGrid>
                    {secondary ? <AuthorSecondary title={secondary}>{secondary}</AuthorSecondary> : null}
                  </MobileCard>
                );
              })}
            </MobileList>
          </>
        )}
      </TableWrap>

      <Pager>
        <PageBtn type="button" disabled={page <= 1 || loading} onClick={() => setPage((x) => Math.max(1, x - 1))}>
          이전
        </PageBtn>
        <span style={{ fontSize: '0.88rem', color: '#475569' }}>
          {page} / {totalPages}
        </span>
        <PageBtn
          type="button"
          disabled={page >= totalPages || loading}
          onClick={() => setPage((x) => Math.min(totalPages, x + 1))}
        >
          다음
        </PageBtn>
      </Pager>

      {error ? <ErrorText>{error}</ErrorText> : null}
    </Wrapper>
  );
};

export default PortalBoardPage;
